import { CEILINGS } from '../../constants';

// Cubes and weight are required, so a resource with neither opens on these rather than empty. They
// are the defaults the trucks table carried (truck_capacity 1200, truck_weight_limit 10000), which
// the resource backfill copied across — the ceilings most converted resources already have.
export const CEILING_DEFAULTS = Object.freeze({ capacity_cubes: 1200, capacity_weight: 10000 });

// Only the columns still null, so a saved value always wins and a cleared field stays cleared.
export const ceilingDefaults = (resource) =>
    Object.fromEntries(Object.entries(CEILING_DEFAULTS).filter(([column]) => resource?.[column] == null));

// Which ceiling is the blocker, and why, so the offending card marks itself.
//
// Zero is rejected, not just negatives: shipper availability checks a cubes or weight ceiling whenever
// it is not NULL, and turns a booking away when its total is strictly over, so a 0 saved as "no
// limit" turns away every booking that carries cubes or weight. Availability does not read stops or
// pieces; they reject a zero too so every ceiling means the same thing, and stay optional — null is
// their "no limit".
export const ceilingFieldErrors = (updated) =>
    Object.fromEntries(
        CEILINGS.map(({ column }) => {
            const value = updated?.[column];

            if (value == null) {
                return [column, column in CEILING_DEFAULTS ? 'Required' : null];
            }

            return [column, value > 0 ? null : 'Must be greater than 0'];
        })
    );

export const capacityCeilingsValid = (updated) => !Object.values(ceilingFieldErrors(updated)).some(Boolean);

const nameKey = (name) => (name || '').trim().toLowerCase();

// Names are unique per client, case-insensitively (resources' unique (client_id, lower(name))).
// Checked here as well so the operator is told which field to change rather than getting a failed
// save; `takenNames` also carries any the database refused that the loaded list did not know about.
export const nameTaken = (name, takenNames = []) =>
    !!nameKey(name) && takenNames.some((taken) => nameKey(taken) === nameKey(name));

// A bound of 0 reads as unset, the way genRecommendations reads it (`timeframe_start &&
// timeframe_end`). So an end of midnight, or a lone start, silently drops the window a shipper is
// offered — as does an end at or before the start.
export const timeframeInvalid = (updated) => {
    const start = updated?.timeframe_start || 0;
    const end = updated?.timeframe_end || 0;

    return (start > 0 || end > 0) && !(end > start);
};

// Why Save is blocked on the Setup tab, per field, so the button and the inputs can never disagree
// about it: a greyed-out Save always has a marked field to explain it. Subregions are optional — a
// resource with none is fleet-only, not shipper-bookable.
//
// `name` is the message itself, since it can fail two ways.
export const setupFieldErrors = ({ updated, scheduleError, takenNames }) => ({
    name: !updated?.name?.trim()
        ? 'Give the resource a name to save'
        : nameTaken(updated.name, takenNames)
        ? `A resource named ${updated.name.trim()} already exists`
        : false,
    boxSize: updated?.box_size != null && updated.box_size < 0,
    // A resource with nothing picked has no schedule at all, so it is a blocker rather than an
    // empty optional field. The message comes from scheduleError, per schedule type.
    schedule: scheduleError || false,
    timeframe: timeframeInvalid(updated),
});

export const setupHasError = (errors) => Object.values(errors).some(Boolean);

// A save the database refused on a duplicate name. The mutation's onError resolves the promise with
// `{ errors }` rather than rejecting. Any uniqueness violation counts: every other unique target
// UPSERT_RESOURCES writes (pkeys, region and partner mappings) has an on_conflict, so the name
// constraint is the only one a save can hit.
export const isDuplicateNameError = (result) =>
    [result?.errors?.message, ...(result?.errors?.graphQLErrors || []).map((error) => error?.message)].some((message) =>
        /uniqueness violation|duplicate key/i.test(message || '')
    );
